// src/components/FormSteps/ResultsStep.tsx
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useFormContext } from '../../context/FormContext';
import { downloadPDF } from '../../services/pdfService';
import { cleanGeneratedText } from '../../utils/textUtils';

const ResultsStep: React.FC = () => {
  const { formData, generatedBylaws, goToStep, resetFormData } = useFormContext();
  const [copied, setCopied] = useState(false);
  
  // Clean the generated bylaws to remove think tags and other artifacts
  const cleanedBylaws = cleanGeneratedText(generatedBylaws);
  
  const handleDownload = () => {
    downloadPDF(cleanedBylaws, formData.coopName);
  };
  
  const handleCopy = () => {
    navigator.clipboard.writeText(cleanedBylaws)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
        alert('Failed to copy text to clipboard');
      });
  };
  
  const handleGoBack = () => {
    goToStep(1);
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all form data? All your information and generated bylaws will be lost.')) {
      resetFormData();
    }
  };
  
  // Custom markdown components for better styling
  const markdownComponents = {
    h1: ({ children }: any) => (
      <h1 className="text-2xl font-bold mt-8 mb-4 text-center border-b-2 border-gray-300 pb-2">
        {children}
      </h1>
    ),
    h2: ({ children }: any) => (
      <h2 className="text-xl font-bold mt-6 mb-3 text-gray-800">
        {children}
      </h2>
    ),
    h3: ({ children }: any) => (
      <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-700">
        {children}
      </h3>
    ),
    p: ({ children }: any) => (
      <p className="my-3 leading-relaxed text-gray-800">
        {children}
      </p>
    ),
    strong: ({ children }: any) => (
      <strong className="font-semibold text-gray-900">
        {children}
      </strong>
    ),
    ul: ({ children }: any) => (
      <ul className="list-disc list-inside my-3 space-y-1 text-gray-800">
        {children}
      </ul>
    ),
    ol: ({ children }: any) => (
      <ol className="list-decimal list-inside my-3 space-y-1 text-gray-800">
        {children}
      </ol>
    ),
    li: ({ children }: any) => (
      <li className="ml-2">
        {children}
      </li>
    ),
    hr: () => (
      <hr className="my-6 border-gray-300" />
    ),
    blockquote: ({ children }: any) => (
      <blockquote className="border-l-4 border-blue-500 pl-4 my-4 italic text-gray-700 bg-blue-50 py-2">
        {children}
      </blockquote>
    ),
    table: ({ children }: any) => (
      <div className="overflow-x-auto my-4">
        <table className="min-w-full border border-gray-300">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }: any) => (
      <thead className="bg-gray-50">
        {children}
      </thead>
    ),
    tbody: ({ children }: any) => (
      <tbody className="divide-y divide-gray-200">
        {children}
      </tbody>
    ),
    tr: ({ children }: any) => (
      <tr className="hover:bg-gray-50">
        {children}
      </tr>
    ),
    th: ({ children }: any) => (
      <th className="px-4 py-2 text-left text-sm font-semibold text-gray-900 border-b border-gray-300">
        {children}
      </th>
    ),
    td: ({ children }: any) => (
      <td className="px-4 py-2 text-sm text-gray-800 border-b border-gray-200">
        {children}
      </td>
    ),
  };
  
  return (
    <div className="p-6 bg-white rounded-lg">
      <h2 className="text-xl font-bold mb-4">Your Co-operative Bylaws</h2>
      
      <div className="flex justify-between mb-4">
        <button 
          onClick={handleDownload}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Download as PDF
        </button>
        
        <button 
          onClick={handleCopy}
          className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
        >
          {copied ? "Copied!" : "Copy Text"}
        </button>
      </div>
      
      <div className="mb-6 p-6 bg-white rounded-lg border border-gray-200 max-h-96 overflow-y-auto shadow-sm prose prose-sm max-w-none">
        {cleanedBylaws ? (
          <ReactMarkdown components={markdownComponents}>
            {cleanedBylaws}
          </ReactMarkdown>
        ) : (
          <div className="text-center text-gray-500 py-8">
            No bylaws generated yet
          </div>
        )}
      </div>
      
      <div className="mt-6 text-sm text-gray-600 mb-6">
        <p>
          <strong>Note:</strong> These bylaws have been generated based on the information provided and research on legal requirements. 
          We strongly recommend having them reviewed by a legal professional familiar with co-operative law 
          in your jurisdiction before filing.
        </p>
      </div>
      
      <div className="flex justify-between">
        <button 
          onClick={handleGoBack}
          className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
        >
          Go Back
        </button>
        <button 
          onClick={handleClearAll}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Clear All
        </button>
      </div>
    </div>
  );
};

export default ResultsStep; 